<template>
	<div class="awsPickerRoot">
		<div class="pickerHeading">
			AWS Target Group Membership
			<input type="button" value="Refresh Lists" @click="loadResources" :disabled="loading" />
		</div>
		<p class="pickerExplain">This monitor alerts when any checked EC2 instance is missing from any checked Target Group for longer than the grace period.</p>
		<div v-if="loading" class="loading"><ScaleLoader /> Loading AWS resources…</div>
		<div v-else-if="error" class="error">
			{{error}}
			<div class="errorHint">The AWS credentials are configured on the admin Settings page.</div>
		</div>
		<template v-else>
			<div class="pickerSection">
				<div class="pickerSectionTitle">Target Groups</div>
				<div v-if="!targetGroups.length" class="pickerEmpty">No Target Groups were found in the configured AWS region.</div>
				<label class="pickerItem" v-for="tg in targetGroups" :key="tg.arn" :title="tg.arn">
					<input type="checkbox" :checked="isTargetGroupSelected(tg.arn)" @change="setTargetGroupSelected(tg.arn, $event.target.checked)" />
					<span class="pickerItemName">{{tg.name}}</span>
					<span class="pickerItemDetail" v-if="tg.protocol">{{tg.protocol}}:{{tg.port}}</span>
				</label>
				<label class="pickerItem orphan" v-for="arn in orphanedTargetGroupArns" :key="arn" :title="arn">
					<input type="checkbox" checked @change="setTargetGroupSelected(arn, $event.target.checked)" />
					<span class="pickerItemName">{{targetGroupNameFromArn(arn)}}</span>
					<span class="pickerItemDetail">not found in AWS — uncheck to remove</span>
				</label>
			</div>
			<div class="pickerSection">
				<div class="pickerSectionTitle">EC2 Instances Expected in the Target Groups</div>
				<div v-if="!instances.length" class="pickerEmpty">No EC2 instances were found in the configured AWS region.</div>
				<label class="pickerItem" v-for="inst in instances" :key="inst.instanceId">
					<input type="checkbox" :checked="isInstanceSelected(inst.instanceId)" @change="setInstanceSelected(inst.instanceId, $event.target.checked)" />
					<span class="pickerItemName">{{inst.name ? inst.name : inst.instanceId}}</span>
					<span class="pickerItemDetail" v-if="inst.name">{{inst.instanceId}}</span>
					<span :class="{ pickerItemDetail: true, stateBad: inst.state !== 'running' }">{{inst.state}}</span>
				</label>
				<label class="pickerItem orphan" v-for="instanceId in orphanedInstanceIds" :key="instanceId">
					<input type="checkbox" checked @change="setInstanceSelected(instanceId, $event.target.checked)" />
					<span class="pickerItemName">{{instanceId}}</span>
					<span class="pickerItemDetail">not found in AWS — uncheck to remove</span>
				</label>
			</div>
		</template>
	</div>
</template>

<script>
	import { GetAwsTargetGroupResources } from 'appRoot/api/MonitoringConfig';

	export default {
		props:
		{
			monitor: {
				type: Object,
				required: true
			}
		},
		data()
		{
			return {
				loading: false,
				error: null,
				targetGroups: [],
				instances: []
			};
		},
		created()
		{
			// Older monitor objects (or ones just created from editSpec defaults) may lack these arrays, and
			// arrays may contain null/empty entries.  Always assign fresh arrays; the editSpec default array
			// instance is shared between monitors.
			this.monitor.AwsTargetGroupArns = Array.isArray(this.monitor.AwsTargetGroupArns) ? this.monitor.AwsTargetGroupArns.filter(a => a) : [];
			this.monitor.AwsInstanceIds = Array.isArray(this.monitor.AwsInstanceIds) ? this.monitor.AwsInstanceIds.filter(id => id) : [];
			this.loadResources();
		},
		computed:
		{
			selectedTargetGroupArns()
			{
				return this.monitor.AwsTargetGroupArns || [];
			},
			selectedInstanceIds()
			{
				return this.monitor.AwsInstanceIds || [];
			},
			orphanedTargetGroupArns()
			{
				let known = {};
				for (let i = 0; i < this.targetGroups.length; i++)
					known[this.targetGroups[i].arn] = true;
				return this.selectedTargetGroupArns.filter(arn => arn && !known[arn]);
			},
			orphanedInstanceIds()
			{
				let known = {};
				for (let i = 0; i < this.instances.length; i++)
					known[this.instances[i].instanceId] = true;
				return this.selectedInstanceIds.filter(id => id && !known[id]);
			}
		},
		methods:
		{
			loadResources()
			{
				this.loading = true;
				this.error = null;
				GetAwsTargetGroupResources()
					.then(data =>
					{
						if (data.success)
						{
							this.targetGroups = data.targetGroups;
							this.instances = data.instances;
						}
						else
							this.error = data.error;
					})
					.catch(err =>
					{
						this.error = err.message;
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			isTargetGroupSelected(arn)
			{
				return this.selectedTargetGroupArns.indexOf(arn) !== -1;
			},
			setTargetGroupSelected(arn, selected)
			{
				// Assign a new array instead of mutating, because the default array from editSpec is a shared instance.
				let arr = this.selectedTargetGroupArns.filter(a => a !== arn);
				if (selected)
					arr.push(arn);
				this.monitor.AwsTargetGroupArns = arr;
			},
			isInstanceSelected(instanceId)
			{
				return this.selectedInstanceIds.indexOf(instanceId) !== -1;
			},
			setInstanceSelected(instanceId, selected)
			{
				let arr = this.selectedInstanceIds.filter(id => id !== instanceId);
				if (selected)
					arr.push(instanceId);
				this.monitor.AwsInstanceIds = arr;
			},
			targetGroupNameFromArn(arn)
			{
				if (!arn)
					return "";
				let idx = arn.indexOf(":targetgroup/");
				if (idx === -1)
					return arn;
				let parts = arn.substr(idx + 1).split('/');
				return parts.length >= 2 ? parts[1] : arn;
			}
		}
	}
</script>

<style scoped>
	.awsPickerRoot
	{
		margin-top: 15px;
		border-top: 1px solid #AAAAAA;
		padding-top: 10px;
		max-width: 500px;
	}

	.pickerHeading
	{
		font-size: 16px;
		font-weight: bold;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
	}

	.pickerExplain
	{
		margin: 5px 0px 10px 0px;
	}

	.loading
	{
		text-align: center;
		margin: 20px 0px;
	}

	.error
	{
		color: #FF0000;
		font-weight: bold;
		margin: 10px 0px;
	}

	.errorHint
	{
		color: #000000;
		font-weight: normal;
		margin-top: 5px;
	}

	.pickerSection
	{
		margin-bottom: 15px;
	}

	.pickerSectionTitle
	{
		font-weight: bold;
		margin-bottom: 4px;
	}

	.pickerEmpty
	{
		font-style: italic;
	}

	.pickerItem
	{
		display: block;
		padding: 2px 4px;
		border-radius: 3px;
		cursor: pointer;
		user-select: none;
	}

		.pickerItem:hover
		{
			background-color: #f0f8ff;
		}

		.pickerItem.orphan
		{
			background-color: #fff3cd;
		}

	.pickerItemName
	{
		margin-left: 4px;
		font-weight: bold;
	}

	.pickerItemDetail
	{
		margin-left: 8px;
		color: #666666;
		font-size: 12px;
	}

	.stateBad
	{
		color: #cc0000;
		font-weight: bold;
	}
</style>
